import { useState } from 'react';

import { AddPropertyV2Model, EnumModel, EnumPropertyModel, NewEntityAsNewPropertyRequest, PrimitiveType, SimplePropertyModel } from '../../../api/api';
import AddEnumPropertyRow from './AddEnumPropertyRow';
import AddPropertyTypeSelector, { PropertyType } from './AddPropertyTypeSelector';
import AddSchemaPropertyRow from './AddSchemaPropertyRow';
import SimplePropertyRow from './AddSimplePropertyRow';

interface Props {
	contextId: string;
	onSave: (property: SimplePropertyModel) => Promise<void> | void;
	onSaveSchemaProperty: (property: NewEntityAsNewPropertyRequest) => Promise<void> | void;
	onSaveReferenceProperty: (property: AddPropertyV2Model) => Promise<void> | void;
	onSaveEnum: (property: EnumPropertyModel) => Promise<void> | void;
	propertyTypes: PrimitiveType[];
	availableEnums: EnumModel[];
	isEditing: boolean;
	onIsEditingChanged: (isEditing: boolean) => void;
}

const AddPropertyRow: React.FC<Props> = ({
	contextId,
	onSave,
	onSaveSchemaProperty,
	onSaveReferenceProperty,
	onSaveEnum,
	propertyTypes,
	availableEnums,
	isEditing,
	onIsEditingChanged,
}) => {
	const [selectedType, setSelectedType] = useState<PropertyType>('Simple');
	const [sharedName, setSharedName] = useState('');
	const [sharedDescription, setSharedDescription] = useState('');
	const [sharedIsRequired, setSharedIsRequired] = useState(false);
	const [sharedIsMultiple, setSharedIsMultiple] = useState(false);

	const sanitizeName = (value: string) => value.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-_]/g, '');

	const handleOnIsEditingChanged = (isEditing: boolean) => {
		if (!isEditing) {
			setSharedName('');
			setSharedDescription('');
			setSharedIsRequired(false);
			setSharedIsMultiple(false);
		}
		onIsEditingChanged(isEditing);
	};

	if (!isEditing) {
		return null;
	}

	return (
		<>
			<AddPropertyTypeSelector onTypeSelected={setSelectedType} />

			{selectedType === 'Simple' && (
				<SimplePropertyRow
					onSave={onSave}
					propertyTypes={propertyTypes}
					onIsEditingChanged={handleOnIsEditingChanged}
					name={sharedName}
					onNameChange={(v) => setSharedName(sanitizeName(v))}
					description={sharedDescription}
					onDescriptionChange={setSharedDescription}
					isRequired={sharedIsRequired}
					onIsRequiredChange={setSharedIsRequired}
					isMultiple={sharedIsMultiple}
					onIsMultipleChange={setSharedIsMultiple}
				/>
			)}

			{selectedType === 'Schema' && (
				<AddSchemaPropertyRow
					onSave={onSaveSchemaProperty}
					onSaveReference={onSaveReferenceProperty}
					contextId={contextId}
					onIsEditingChanged={handleOnIsEditingChanged}
					name={sharedName}
					onNameChange={(v) => setSharedName(sanitizeName(v))}
					description={sharedDescription}
					onDescriptionChange={setSharedDescription}
					isRequired={sharedIsRequired}
					onIsRequiredChange={setSharedIsRequired}
					isMultiple={sharedIsMultiple}
					onIsMultipleChange={setSharedIsMultiple}
				/>
			)}

			{selectedType === 'Enum' && (
				<AddEnumPropertyRow
					onSave={onSaveEnum}
					onIsEditingChanged={handleOnIsEditingChanged}
					availableEnums={availableEnums}
					name={sharedName}
					onNameChange={(v) => setSharedName(sanitizeName(v))}
					description={sharedDescription}
					onDescriptionChange={setSharedDescription}
					isRequired={sharedIsRequired}
					onIsRequiredChange={setSharedIsRequired}
					isMultiple={sharedIsMultiple}
					onIsMultipleChange={setSharedIsMultiple}
				/>
			)}
		</>
	);
};

export default AddPropertyRow;
